import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Filter, Eye, Pencil, Trash2, FolderKanban, Upload } from 'lucide-react';
import { ImportLeadsModal } from '../components/ImportLeadsModal';

const STATUS_OPTIONS = [
  { value: 'neu', label: 'Neu' },
  { value: 'kontaktiert', label: 'Kontaktiert' },
  { value: 'termin', label: 'Termin' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'laufend', label: 'Laufend' },
  { value: 'abgelehnt', label: 'Abgelehnt' }
];

const SCORE_OPTIONS = [
  { value: 'heiss', label: 'Heiß' },
  { value: 'mittel', label: 'Mittel' },
  { value: 'kalt', label: 'Kalt' }
];

const QUELLE_OPTIONS = [
  'Jobportal', 'Google', 'LinkedIn', 'Empfehlung', 'Messe', 'Website', 'Sonstige'
];

const getStatusBadgeClass = (status) => {
  const classes = {
    neu: 'bg-gray-100 text-gray-800',
    kontaktiert: 'bg-blue-100 text-blue-800',
    termin: 'bg-purple-100 text-purple-800',
    pilot: 'bg-orange-100 text-orange-800',
    laufend: 'bg-green-100 text-green-800',
    abgelehnt: 'bg-red-100 text-red-800'
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

const getScoreBadgeClass = (score) => {
  const classes = {
    heiss: 'bg-red-500 text-white',
    mittel: 'bg-orange-400 text-white',
    kalt: 'bg-blue-500 text-white'
  };
  return classes[score] || 'bg-gray-400 text-white';
};

export const Leads = () => {
  const navigate = useNavigate();
  const { get, post, put, del } = useApi();
  const { isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    firma: '',
    standort: '',
    branche: '',
    quelle: 'Website',
    ansprechpartner: '',
    rolle: '',
    telefon: '',
    email: '',
    lead_status: 'neu',
    lead_score: 'mittel',
    naechster_schritt: '',
    notizen: '',
    campaign_id: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load campaigns first
      const campaignsData = await get('/campaigns');
      setCampaigns(campaignsData);
      
      // Build leads endpoint
      let endpoint = '/leads?';
      if (statusFilter && statusFilter !== 'all') endpoint += `status=${statusFilter}&`;
      if (scoreFilter && scoreFilter !== 'all') endpoint += `score=${scoreFilter}&`;
      if (campaignFilter && campaignFilter !== 'all') endpoint += `campaign_id=${campaignFilter}&`;
      if (search) endpoint += `search=${search}&`;
      
      const leadsData = await get(endpoint);
      setLeads(leadsData);
    } catch (error) {
      toast.error('Fehler beim Laden der Leads');
    } finally {
      setLoading(false);
    }
  }, [get, statusFilter, scoreFilter, campaignFilter, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({
      firma: '',
      standort: '',
      branche: '',
      quelle: 'Website',
      ansprechpartner: '',
      rolle: '',
      telefon: '',
      email: '',
      lead_status: 'neu',
      lead_score: 'mittel',
      naechster_schritt: '',
      notizen: '',
      campaign_id: campaigns.length > 0 ? campaigns[0].id : ''
    });
    setEditingLead(null);
  };

  const openEditDialog = (lead) => {
    setEditingLead(lead);
    setFormData({
      firma: lead.firma || '',
      standort: lead.standort || '',
      branche: lead.branche || '',
      quelle: lead.quelle || 'Website',
      ansprechpartner: lead.ansprechpartner || '',
      rolle: lead.rolle || '',
      telefon: lead.telefon || '',
      email: lead.email || '',
      lead_status: lead.lead_status || 'neu',
      lead_score: lead.lead_score || 'mittel',
      naechster_schritt: lead.naechster_schritt || '',
      notizen: lead.notizen || '',
      campaign_id: lead.campaign_id || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        await put(`/leads/${editingLead.id}`, formData);
        toast.success('Lead aktualisiert');
      } else {
        await post('/leads', formData);
        toast.success('Lead erstellt');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Lead löschen möchten?')) return;
    try {
      await del(`/leads/${leadId}`);
      toast.success('Lead gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
        <div className="flex gap-2">
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={() => setImportModalOpen(true)}
              data-testid="import-leads-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              Excel Import
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#E63946] hover:bg-[#D62839]" data-testid="create-lead-button">
                <Plus className="h-4 w-4 mr-2" />
                Neuer Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingLead ? 'Lead bearbeiten' : 'Neuer Lead'}</DialogTitle>
                <DialogDescription>
                  {editingLead ? 'Aktualisieren Sie die Lead-Informationen' : 'Fügen Sie einen neuen Lead hinzu'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
                {/* Campaign Selection - only for admin */}
                {isAdmin && campaigns.length > 0 && (
                  <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                    <Label htmlFor="campaign" className="flex items-center gap-2">
                      <FolderKanban className="h-4 w-4 text-[#E63946]" />
                      Kampagne *
                    </Label>
                    <Select 
                      value={formData.campaign_id} 
                      onValueChange={(value) => setFormData({ ...formData, campaign_id: value })}
                    >
                      <SelectTrigger data-testid="lead-campaign-select">
                        <SelectValue placeholder="Kampagne auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firma">Firma *</Label>
                  <Input
                    id="firma"
                    value={formData.firma}
                    onChange={(e) => setFormData({ ...formData, firma: e.target.value })}
                    required
                    data-testid="lead-firma-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="standort">Standort</Label>
                  <Input
                    id="standort"
                    value={formData.standort}
                    onChange={(e) => setFormData({ ...formData, standort: e.target.value })}
                    data-testid="lead-standort-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="branche">Branche</Label>
                  <Input
                    id="branche"
                    value={formData.branche}
                    onChange={(e) => setFormData({ ...formData, branche: e.target.value })}
                    data-testid="lead-branche-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quelle">Quelle *</Label>
                  <Select 
                    value={formData.quelle} 
                    onValueChange={(value) => setFormData({ ...formData, quelle: value })}
                  >
                    <SelectTrigger data-testid="lead-quelle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QUELLE_OPTIONS.map(q => (
                        <SelectItem key={q} value={q}>{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ansprechpartner">Ansprechpartner *</Label>
                  <Input
                    id="ansprechpartner"
                    value={formData.ansprechpartner}
                    onChange={(e) => setFormData({ ...formData, ansprechpartner: e.target.value })}
                    required
                    data-testid="lead-ansprechpartner-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rolle">Rolle</Label>
                  <Input
                    id="rolle"
                    value={formData.rolle}
                    onChange={(e) => setFormData({ ...formData, rolle: e.target.value })}
                    data-testid="lead-rolle-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telefon">Telefon</Label>
                  <Input
                    id="telefon"
                    type="tel"
                    value={formData.telefon}
                    onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                    data-testid="lead-telefon-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="lead-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead_status">Status</Label>
                  <Select 
                    value={formData.lead_status} 
                    onValueChange={(value) => setFormData({ ...formData, lead_status: value })}
                  >
                    <SelectTrigger data-testid="lead-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lead_score">Score</Label>
                  <Select 
                    value={formData.lead_score} 
                    onValueChange={(value) => setFormData({ ...formData, lead_score: value })}
                  >
                    <SelectTrigger data-testid="lead-score-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCORE_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="naechster_schritt">Nächster Schritt</Label>
                <Input
                  id="naechster_schritt"
                  value={formData.naechster_schritt}
                  onChange={(e) => setFormData({ ...formData, naechster_schritt: e.target.value })}
                  placeholder="z.B. Follow-up Call am..."
                  data-testid="lead-naechster-schritt-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notizen">Notizen</Label>
                <textarea
                  id="notizen"
                  className="w-full min-h-[100px] p-3 border border-gray-200 rounded-md focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none"
                  value={formData.notizen}
                  onChange={(e) => setFormData({ ...formData, notizen: e.target.value })}
                  data-testid="lead-notizen-input"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-[#E63946] hover:bg-[#D62839]" data-testid="lead-submit-button">
                  {editingLead ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="leads-filters">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Suche nach Firma, Ansprechpartner, Lead-ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="leads-search-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="leads-status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={scoreFilter} onValueChange={setScoreFilter}>
                <SelectTrigger className="w-[150px]" data-testid="leads-score-filter">
                  <SelectValue placeholder="Score" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Scores</SelectItem>
                  {SCORE_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isAdmin && campaigns.length > 0 && (
                <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="leads-campaign-filter">
                    <FolderKanban className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Kampagne" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kampagnen</SelectItem>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card data-testid="leads-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946] mx-auto"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>Keine Leads gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="leads-table">
                <thead>
                  <tr>
                    <th>Lead-ID</th>
                    <th>Firma</th>
                    <th>Ansprechpartner</th>
                    {isAdmin && <th>Kampagne</th>}
                    <th>Quelle</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Letzter Kontakt</th>
                    <th>Nächster Schritt</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                      <td>
                        <span className="font-mono text-sm text-gray-600">{lead.lead_id}</span>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900">{lead.firma}</p>
                          {lead.standort && (
                            <p className="text-sm text-gray-500">{lead.standort}</p>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="text-gray-900">{lead.ansprechpartner}</p>
                          {lead.rolle && (
                            <p className="text-sm text-gray-500">{lead.rolle}</p>
                          )}
                        </div>
                      </td>
                      {isAdmin && (
                        <td>
                          {lead.campaign_name ? (
                            <Badge variant="outline" className="text-xs">
                              {lead.campaign_name}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                      )}
                      <td className="text-gray-600">{lead.quelle}</td>
                      <td>
                        <Badge className={getStatusBadgeClass(lead.lead_status)}>
                          {STATUS_OPTIONS.find(s => s.value === lead.lead_status)?.label || lead.lead_status}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={getScoreBadgeClass(lead.lead_score)}>
                          {SCORE_OPTIONS.find(s => s.value === lead.lead_score)?.label || lead.lead_score}
                        </Badge>
                      </td>
                      <td className="text-gray-600">{formatDate(lead.letzter_kontakt)}</td>
                      <td className="text-gray-600 max-w-[200px] truncate">
                        {lead.naechster_schritt || '-'}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            data-testid={`view-lead-${lead.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(lead)}
                            data-testid={`edit-lead-${lead.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(lead.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Modal */}
      <ImportLeadsModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        onImportComplete={loadData}
      />
    </div>
  );
};
